"use client";

import { useState, useEffect, useRef, useLayoutEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Facebook, Instagram } from "lucide-react";
// import AnimatedWordCycle from "@/components/ui/animated-text-cycle";
import { m } from "framer-motion";
// import { ContactModal } from "@/components/ui/contact-modal";
import dynamic from 'next/dynamic';
import { categories } from "@/lib/catalog";

const LazyContactModal = dynamic(
  () => import('@/components/ui/contact-modal').then(m => m.ContactModal),
  { ssr: false }
);

export function Header({
  bottomNav,
  prioritizeLogo = true,
}: {
  bottomNav?: React.ReactNode;
  prioritizeLogo?: boolean;
}) {
  // Keep an SSR-safe initial offset aligned with the real rendered header height
  // (logo is 2077x612 -> 250x74, plus py-2/py-4 and 48px bottomNav when present)
  // so pages do not jump after hydration.
  const INITIAL_HEADER_OFFSET_WITH_BOTTOM_NAV = 138;
  const INITIAL_HEADER_OFFSET_NO_BOTTOM_NAV = 122;
  const INITIAL_TOTAL_HEIGHT = bottomNav
    ? INITIAL_HEADER_OFFSET_WITH_BOTTOM_NAV
    : INITIAL_HEADER_OFFSET_NO_BOTTOM_NAV;
  const INITIAL_BOTTOM_ROW_HEIGHT = bottomNav ? 48 : 0;
  const INITIAL_TOP_ROW_HEIGHT = Math.max(
    INITIAL_TOTAL_HEIGHT - INITIAL_BOTTOM_ROW_HEIGHT,
    0
  );

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const headerContainerRef = useRef<HTMLDivElement | null>(null);
  const topRowRef = useRef<HTMLDivElement | null>(null);
  const bottomRowRef = useRef<HTMLDivElement | null>(null);
  const productsZoneRef = useRef<HTMLDivElement | null>(null);
  // We keep a timeout ref so the dropdown can wait before closing.
  const productsHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [topRowHeight, setTopRowHeight] = useState(INITIAL_TOP_ROW_HEIGHT);
  const [bottomRowHeight, setBottomRowHeight] = useState(INITIAL_BOTTOM_ROW_HEIGHT);
  const totalHeight = bottomRowHeight + (showNavbar ? topRowHeight : 0);
  const appliedHeaderOffsetRef = useRef<number | null>(null);

  const toggleMenu = () => {
    const newMenuState = !isMenuOpen;
    setIsMenuOpen(newMenuState);
    
    // Prevent body scroll when menu is open
    if (newMenuState) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  // Hover helpers keep the Products dropdown steady while users move the mouse.
  const cancelProductsClose = () => {
    if (productsHoverTimeout.current) {
      clearTimeout(productsHoverTimeout.current);
      productsHoverTimeout.current = null;
    }
  };

  const keepProductsMenuOpen = () => {
    cancelProductsClose();
    setIsProductsOpen(true);
  };

  const scheduleProductsClose = () => {
    // Delay closing so the pointer can travel from the button to the list.
    cancelProductsClose();
    productsHoverTimeout.current = setTimeout(() => {
      setIsProductsOpen(false);
      productsHoverTimeout.current = null;
    }, 200);
  };

  const isTargetInsideProductsZone = (target: EventTarget | null) => {
    if (!target) {
      return false;
    }
    return productsZoneRef.current?.contains(target as Node) ?? false;
  };

  const handleProductsPointerLeave = (event: React.PointerEvent<HTMLElement>) => {
    // Ignore pointer transitions that stay within the combined Products zone.
    if (isTargetInsideProductsZone(event.relatedTarget)) {
      return;
    }
    scheduleProductsClose();
  };

  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  // Clean up body scroll on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      cancelProductsClose();
    };
  }, []);

  // Expose a CSS variable with current total height for pages without bottomNav
  // (e.g. /blog) that offset main content via var(--header-offset).
  // Pages with bottomNav (/products) use responsive CSS padding directly to avoid
  // post-hydration style invalidation and forced reflows.
  useLayoutEffect(() => {
    if (bottomNav || totalHeight <= 0) {
      return;
    }
    if (appliedHeaderOffsetRef.current === totalHeight) {
      return;
    }
    document.documentElement.style.setProperty('--header-offset', `${totalHeight}px`);
    appliedHeaderOffsetRef.current = totalHeight;
    return () => {
      document.documentElement.style.removeProperty('--header-offset');
      appliedHeaderOffsetRef.current = null;
    };
  }, [bottomNav, totalHeight]);

  // Handle scroll event to show/hide navbar with smooth transition
  // Optimizations for INP (desktop-only):
  // - Passive listener to avoid blocking scrolling
  // - Throttle updates via requestAnimationFrame to minimize state churn
  // - Only attach on desktop (>=768px) to avoid impacting mobile (already fine)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');

    let rafId = 0;
    let pending = false;

    const updateNavbar = () => {
      pending = false;
      const currentScrollY = window.scrollY;
      if (currentScrollY < 10) {
        setShowNavbar(true);
      } else if (currentScrollY < lastScrollY) {
        setShowNavbar(true);
      } else {
        setShowNavbar(false);
      }
      setLastScrollY(currentScrollY);
    };

    const onScroll = () => {
      if (pending) return;
      pending = true;
      rafId = window.requestAnimationFrame(updateNavbar);
    };

    const attach = () => {
      if (mediaQuery.matches) {
        window.addEventListener('scroll', onScroll, { passive: true });
      }
    };

    const detach = () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      pending = false;
    };

    attach();
    const onChange = () => {
      detach();
      attach();
    };
    mediaQuery.addEventListener('change', onChange);

    return () => {
      mediaQuery.removeEventListener('change', onChange);
      detach();
    };
  }, [lastScrollY]);

  // Read row heights from ResizeObserver entries instead of querying offsetHeight
  // synchronously after DOM mutations, which eliminates forced reflows on page load.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const borderBoxHeight = entry.borderBoxSize?.[0]?.blockSize;
        const measuredHeight = Math.round(
          typeof borderBoxHeight === 'number'
            ? borderBoxHeight
            : entry.contentRect.height
        );
        if (measuredHeight <= 0) continue;

        if (entry.target === topRowRef.current) {
          setTopRowHeight((current) => (current === measuredHeight ? current : measuredHeight));
        } else if (entry.target === bottomRowRef.current) {
          setBottomRowHeight((current) => (current === measuredHeight ? current : measuredHeight));
        }
      }
    });

    if (topRowRef.current) ro.observe(topRowRef.current);
    if (bottomRowRef.current) ro.observe(bottomRowRef.current);

    return () => {
      ro.disconnect();
    };
  }, []);

  return (
    <>
      {/* Mobile Menu Backdrop */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={toggleMenu}
        />
      )}

      <div 
        ref={headerContainerRef}
        className={`fixed top-0 left-0 right-0 z-50 bg-black/40 backdrop-blur-lg border-b border-white/5 ${
          bottomNav
            ? 'h-28 sm:h-[130px] md:h-[138px]'
            : 'h-20 sm:h-[114px] md:h-[122px]'
        }`}
        style={showNavbar ? undefined : { height: `${bottomRowHeight}px` }}
      >
        <m.div
          ref={topRowRef}
          className={`absolute top-0 left-0 right-0 flex items-center justify-between px-4 sm:px-8 ${
            bottomNav
              ? 'py-2 h-16 sm:h-[82px] md:h-[90px]'
              : 'py-4 sm:py-6 h-20 sm:h-[114px] md:h-[122px]'
          }`}
          initial={false}
          animate={{ y: showNavbar ? 0 : -(topRowHeight || 100) }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
        >
          <div className="flex items-center gap-6">
          <Link href="/" className="relative group">
            <m.div 
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              {/* Use the real file ratio (2077x612). CSS still sets the visible size
                  (w-40 / sm:w-56 / md:w-[250px]). Matching props avoid Next.js warning
                  when only one of width/height differs from the HTML attributes. */}
              <Image 
                src="/images/amp_noURL_blue.png" 
                alt="Amplified Branding Logo" 
                width={2077} 
                height={612}
                sizes="(max-width: 640px) 160px, (max-width: 768px) 224px, 250px"
                priority={prioritizeLogo}
                className="w-40 sm:w-56 md:w-[250px] h-auto transition-all duration-300"
                // Keep initial server/client inline style consistent to avoid hydration style diffs.
                style={{ height: "auto" }}
              />
            </m.div>
            <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#8AD4E1] group-hover:w-[102.5%] transition-all duration-300" />
          </Link>
          
          </div>

          {/* Desktop & Mobile Navigation */}
          <div className="flex items-center gap-4">
          {/* Social Media Icons */}
          <div className="hidden sm:flex space-x-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a href="https://www.facebook.com/profile.php?id=61564824001439" target="_blank" rel="noopener noreferrer" className="rounded-full group">
                    <Button variant="ghost" size="icon" className="w-12 h-12 group-hover:bg-white/10 transition-colors duration-300">
                      <Facebook className="h-5 w-5 text-white group-hover:text-[#8AD4E1] transition-colors duration-300" />
                      <span className="sr-only">Facebook</span>
                    </Button>
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Follow us on Facebook</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a href="https://x.com/AmpBrandAZ" target="_blank" rel="noopener noreferrer" className="rounded-full group">
                    <Button variant="ghost" size="icon" className="w-12 h-12 group-hover:bg-white/10 transition-colors duration-300">
                      <svg className="h-5 w-5 text-white group-hover:text-[#8AD4E1] transition-colors duration-300" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      <span className="sr-only">X</span>
                    </Button>
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Follow us on X</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a href="https://www.instagram.com/amplifiedbranding.az/" target="_blank" rel="noopener noreferrer" className="rounded-full group">
                    <Button variant="ghost" size="icon" className="w-12 h-12 group-hover:bg-white/10 transition-colors duration-300">
                      <Instagram className="h-5 w-5 text-white group-hover:text-[#8AD4E1] transition-colors duration-300" />
                      <span className="sr-only">Instagram</span>
                    </Button>
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Follow us on Instagram</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {/* Temporarily hidden - Shop feature not ready yet */}
          {false && <div className="hidden sm:block h-6 w-px bg-white/20"></div>}
          
          {/* Shop Link - Desktop Only - Temporarily hidden */}
          {false && (
            <Link href="/shop" className="hidden sm:block">
              <Button 
                variant="ghost"
                className="text-white hover:text-[#8AD4E1] hover:bg-white/10 px-4 sm:px-6 py-2 sm:py-3 rounded-md text-sm sm:text-base font-medium transition duration-300 ease-in-out"
              >
                Shop
              </Button>
            </Link>
          )}

          {/* 
            Products Dropdown - Desktop Only
            Temporarily hidden from the live header while the /products experience
            is still in progress. We keep the JSX here (behind `false &&`) so
            re-enabling it later is as simple as flipping the flag or removing the
            conditional.
          */}
          {false && (
            <div 
              ref={productsZoneRef}
              className="relative hidden sm:block"
              onPointerEnter={keepProductsMenuOpen}
              onPointerLeave={handleProductsPointerLeave}
            >
              <Link href="/products" className="hidden sm:block">
                <Button
                  variant="ghost"
                  className="text-white hover:text-[#8AD4E1] hover:bg-white/10 px-4 sm:px-6 py-2 sm:py-3 rounded-md text-sm sm:text-base font-medium transition duration-300 ease-in-out"
                  aria-haspopup="menu"
                  aria-expanded={isProductsOpen}
                  aria-controls="products-menu"
                >
                  Products
                </Button>
              </Link>
              {/* We give the dropdown an explicit z-index so it can sit above the Products subnav on the /products route. */}
              <div
                id="products-menu"
                className={`absolute right-0 top-full w-64 bg-black/90 backdrop-blur-sm border border-zinc-700 rounded-md shadow-lg z-50 overflow-hidden transition-all origin-top-right ${isProductsOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}
                role="menu"
                onPointerEnter={keepProductsMenuOpen}
                onPointerLeave={handleProductsPointerLeave}
              >
                <div className="py-2">
                  {categories.map((c) => (
                    <Link
                      key={c.slug}
                      href={`/products#${c.slug}`}
                      className="block px-4 py-2 text-sm text-white hover:bg-white/10 hover:text-[#8AD4E1] transition-colors"
                      role="menuitem"
                      onClick={(e) => {
                        setIsProductsOpen(false);
                        try {
                          if (typeof window !== "undefined" && window.location.pathname === "/products") {
                            e.preventDefault();
                            const el = document.getElementById(c.slug);
                            if (el) {
                              el.scrollIntoView({ behavior: "smooth", block: "start" });
                              history.replaceState(null, "", `/products#${c.slug}`);
                            }
                          }
                        } catch {
                          // no-op
                        }
                      }}
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Keep Products and Blog grouped tightly so the center nav feels cleaner. */}
          <div className="hidden sm:flex items-center gap-1">
            {/* Products button - Desktop only revisit so visitors can reach catalog */}
            <Button
              asChild
              variant="ghost"
              className="text-white hover:text-[#8AD4E1] hover:bg-white/10 px-2 sm:px-3 py-2 sm:py-3 rounded-md text-sm sm:text-base font-bold transition duration-300 ease-in-out"
            >
              {/* Keep the anchor full height so button content stays centered */}
              <Link
                href="/products"
                title="Explore our products catalog"
                className="flex h-full items-center justify-center"
              >
                Products
              </Link>
            </Button>

            {/* Blog — Markdown-backed articles at /blog */}
            <Button
              asChild
              variant="ghost"
              className="text-white hover:text-[#8AD4E1] hover:bg-white/10 px-2 sm:px-3 py-2 sm:py-3 rounded-md text-sm sm:text-base font-bold transition duration-300 ease-in-out"
            >
              <Link
                href="/blog"
                title="Read our blog"
                className="flex h-full items-center justify-center"
              >
                Blog
              </Link>
            </Button>
          </div>

          {/* Contact Us button - Visible on all devices */}
          <Button 
            className="flex bg-[#8AD4E1] hover:bg-[#7BC0CD] text-black px-3 sm:px-6 py-2 sm:py-3 rounded-md text-xs sm:text-base font-bold transition duration-300 ease-in-out transform hover:scale-105 shadow-md hover:shadow-lg"
            onClick={openContactModal}
          >
            Contact Us
          </Button>

          {/* Enhanced Menu Button with micro-interactions */}
          <div className="relative sm:hidden">
            <Button 
              variant="ghost" 
              className="relative z-20 p-3 focus:outline-none"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              <div className="flex flex-col justify-center items-center">
                <span className={`block w-6 h-0.5 bg-white mb-2 transition-transform ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                <span className={`block w-6 h-0.5 bg-white transition-opacity ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`block w-6 h-0.5 bg-white mt-2 transition-transform ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
              </div>
            </Button>

            {/* Menu Dropdown with animations */}
            <div id="mobile-menu" className={`absolute right-0 top-full mt-2 w-64 bg-black/90 backdrop-blur-sm border border-zinc-700 rounded-md shadow-lg transition-all origin-top-right z-60 ${isMenuOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'}`}>
              <div className="py-4 px-4">
                  {/* Main Navigation Links */}
                <div className="space-y-2 mb-4">
                  {/* 
                    Products - Mobile: temporarily hidden from the live header.
                    We keep the code wrapped in `false &&` so that when the
                    /products flow is ready, it can be turned back on quickly.
                  */}
                  {false && (
                    <details className="group">
                      <summary className="flex w-full cursor-pointer list-none items-center justify-between rounded-md px-3 py-3 text-base text-white hover:bg-white/10 hover:text-[#8AD4E1] transition-colors">
                        <span className="flex items-center gap-3">
                          <span className="text-[#8AD4E1]">🧢</span>
                          Products
                        </span>
                        <span className="text-zinc-400 group-open:rotate-180 transition-transform">⌄</span>
                      </summary>
                      <div className="mt-2 space-y-1">
                        {categories.map((c) => (
                          <Link
                            key={c.slug}
                            href={`/products#${c.slug}`}
                            className="block rounded-md px-8 py-2 text-sm text-white hover:bg-white/10 hover:text-[#8AD4E1] transition-colors"
                            onClick={(e) => {
                              toggleMenu();
                              try {
                                if (typeof window !== "undefined" && window.location.pathname === "/products") {
                                  e.preventDefault();
                                  const el = document.getElementById(c.slug);
                                  if (el) {
                                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                                    history.replaceState(null, "", `/products#${c.slug}`);
                                  }
                                }
                              } catch {
                                // no-op
                              }
                            }}
                          >
                            {c.name}
                          </Link>
                        ))}
                      </div>
                    </details>
                  )}

                  {/* Products Link - Mobile so visitors can reach catalog from menu */}
                  <Link 
                    href="/products" 
                    className="block py-3 px-3 text-base text-white hover:bg-white/10 rounded-md hover:text-[#8AD4E1] transition-colors flex items-center gap-3"
                    onClick={toggleMenu}
                  >
                    <span className="text-[#8AD4E1]">🧢</span>
                    Products
                  </Link>

                  <Link
                    href="/blog"
                    className="block py-3 px-3 text-base text-white hover:bg-white/10 rounded-md hover:text-[#8AD4E1] transition-colors flex items-center gap-3"
                    onClick={toggleMenu}
                  >
                    <span className="text-[#8AD4E1]">✎</span>
                    Blog
                  </Link>

                  {/* Shop Link - Mobile - Temporarily hidden */}
                  {false && (
                    <Link 
                      href="/shop" 
                      className="block py-3 px-3 text-base text-white hover:bg-white/10 rounded-md hover:text-[#8AD4E1] transition-colors flex items-center gap-3"
                      onClick={toggleMenu}
                    >
                      <span className="text-[#8AD4E1]">🛍️</span>
                      Shop
                    </Link>
                  )}
                </div>

                {/* Social Media Links */}
                <div className="pt-4 mt-4 border-t border-zinc-700">
                  <p className="text-xs font-medium text-zinc-400 mb-3 px-3">Follow Us</p>
                  <div className="flex justify-center gap-4 px-3">
                    <a 
                      href="https://www.facebook.com/profile.php?id=61564824001439" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2 text-white hover:bg-white/10 rounded-md hover:text-[#8AD4E1] transition-colors"
                      onClick={toggleMenu}
                      aria-label="Follow us on Facebook"
                    >
                      <Facebook className="h-5 w-5" />
                    </a>
                    <a 
                      href="https://x.com/AmpBrandAZ" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2 text-white hover:bg-white/10 rounded-md hover:text-[#8AD4E1] transition-colors"
                      onClick={toggleMenu}
                      aria-label="Follow us on X"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </a>
                    <a 
                      href="https://www.instagram.com/amplifiedbranding.az/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2 text-white hover:bg-white/10 rounded-md hover:text-[#8AD4E1] transition-colors"
                      onClick={toggleMenu}
                      aria-label="Follow us on Instagram"
                    >
                      <Instagram className="h-5 w-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Close desktop/mobile navigation wrapper */}
          </div>
        </m.div>
        {bottomNav && (
          <div ref={bottomRowRef} className="absolute bottom-0 left-0 right-0">
            {bottomNav}
          </div>
        )}
      </div>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <LazyContactModal isOpen={isContactModalOpen} onClose={closeContactModal} />
      )}
    </>
  );
} 